import React, { useState, useEffect, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { toastify } from '../../../../../../utils/toast';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const EMPTY_COUNTS = {
    totalMeters: 0,
    totalInstalled: 0,
    liveStatus: { on: 0, off: 0 },
    meterLiveStatus: { online: 0, offline: 0 },
};

const CommercialMetersTab = () => {
    const navigate = useNavigate();
    const [powerMeters, setPowerMeters] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [facilityCounts, setFacilityCounts] = useState(EMPTY_COUNTS);
    const [countsLoading, setCountsLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    const fetchFacilityCounts = async () => {
        if (!selectedFacilityId) return;
        try {
            setCountsLoading(true);
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/power_meters/facility_meters_count/${selectedFacilityId}?meterType=commercial`,
                'GET'
            );
            if (response?.success && response.data?.data) setFacilityCounts(response.data.data);
        } catch (err) {
            console.error('Error fetching facility counts:', err);
        } finally {
            setCountsLoading(false);
        }
    };

    // Single, unpaginated fetch — search/sort/paginate/export all happen in the UI
    const fetchPowerMeters = async () => {
        if (!selectedFacilityId) {
            toastify('No facility ID available', 'error');
            return;
        }
        try {
            setLoading(true);
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/power_meters/get_all/${selectedFacilityId}?meterType=commercial`,
                'GET'
            );
            if (response.success) {
                const meters = response.data?.data;
                setPowerMeters(Array.isArray(meters) ? meters : []);
            } else {
                toastify(response?.error || 'Failed to fetch power meters', 'error');
                setPowerMeters([]);
            }
        } catch (error) {
            console.error('Error fetching power meters:', error);
            toastify('An error occurred while fetching power meters', 'error');
            setPowerMeters([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedFacilityId) {
            fetchFacilityCounts();
            fetchPowerMeters();
        }
    }, [selectedFacilityId]);

    const filteredMeters = useMemo(() => {
        if (!searchTerm) return powerMeters;
        const q = searchTerm.toLowerCase();
        return powerMeters.filter((m) =>
            String(m.deviceId ?? '').toLowerCase().includes(q) ||
            (m.description ?? '').toLowerCase().includes(q) ||
            (m.unitName ?? '').toLowerCase().includes(q) ||
            (m.unitInfo?.unitName ?? '').toLowerCase().includes(q) ||
            (m.meterSerialNumber ?? '').toLowerCase().includes(q) ||
            String(m.accountNumber ?? '').toLowerCase().includes(q)
        );
    }, [powerMeters, searchTerm]);

    const meterStatusTemplate = (rowData) => {
        const map = { on: 'success', off: 'danger', maintenance: 'warning', faulty: 'danger' };
        const s = (rowData.liveStatus || '').toLowerCase();
        return <span className={`badge rounded-pill text-bg-${map[s] || 'secondary'}`}>{rowData.liveStatus || 'Off'}</span>;
    };

    const meterLiveStatusTemplate = (rowData) => {
        const map = { online: 'success', offline: 'danger', maintenance: 'warning', faulty: 'danger' };
        const s = (rowData.meterLiveStatus || '').toLowerCase();
        return <span className={`badge rounded-pill text-bg-${map[s] || 'secondary'}`}>{rowData.meterLiveStatus || 'Offline'}</span>;
    };

    const descriptionTemplate = (rowData) => rowData.unitInfo?.unitName || rowData.description || '-';

    const actionsTemplate = (rowData) => (
        <Button
            icon="ti ti-eye"
            className="p-button-rounded p-button-text"
            onClick={() => navigate(`/facility/utility_management/meter_management/view_power_meter/${rowData._id}`)}
            tooltip="View Meter"
        />
    );

    // ── Export: Unit, Meter Number, Account Number only ─────────────
    const getExportRows = () => filteredMeters.map((m) => ({
        Unit: m.unitInfo?.unitName || m.description || '-',
        'Meter Number': m.meterSerialNumber || '-',
        'Account Number': m.accountNumber ?? '-',
    }));

    const handleExportExcel = () => {
        setExporting(true);
        try {
            const rows = getExportRows();
            const ws = XLSX.utils.json_to_sheet(rows);
            ws['!cols'] = [{ wch: 28 }, { wch: 20 }, { wch: 18 }];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Commercial Meters');
            XLSX.writeFile(wb, `Commercial_Power_Meters_${new Date().toISOString().split('T')[0]}.xlsx`);
        } finally {
            setExporting(false);
        }
    };

    const handleExportPDF = () => {
        setExporting(true);
        try {
            const rows = getExportRows();
            const doc = new jsPDF('p', 'mm', 'a4');
            doc.setFontSize(14);
            doc.text('Commercial Power Meters', 14, 15);
            doc.autoTable({
                startY: 22,
                head: [['Unit', 'Meter Number', 'Account Number']],
                body: rows.map((r) => [r.Unit, r['Meter Number'], r['Account Number']]),
                styles: { fontSize: 9 },
                headStyles: { fillColor: [30, 64, 175] },
            });
            doc.save(`Commercial_Power_Meters_${new Date().toISOString().split('T')[0]}.pdf`);
        } finally {
            setExporting(false);
        }
    };

    const SummaryBar = () => {
        if (countsLoading) {
            return (
                <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    <span className="text-muted small">Loading counts...</span>
                </div>
            );
        }
        const { totalMeters, totalInstalled, liveStatus, meterLiveStatus } = facilityCounts;
        return (
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-3 rounded"
                style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                <div className="d-flex flex-column align-items-center px-3 py-1 rounded bg-white border">
                    <span className="fw-bold fs-5">{totalMeters}</span>
                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>Total Meters</span>
                </div>
                <div className="d-flex flex-column align-items-center px-3 py-1 rounded bg-white border">
                    <span className="fw-bold fs-5">{totalInstalled}</span>
                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>Installed</span>
                </div>
                <div className="vr mx-1" style={{ height: 40 }} />
                <span className="text-muted small fw-semibold">POWER STATUS:</span>
                <div className="d-flex align-items-center gap-1 px-3 py-1 rounded" style={{ background: '#d1e7dd', border: '1px solid #a3cfbb' }}>
                    <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>ON</span>
                    <span className="fw-bold text-success fs-6">{liveStatus.on}</span>
                </div>
                <div className="d-flex align-items-center gap-1 px-3 py-1 rounded" style={{ background: '#f8d7da', border: '1px solid #f1aeb5' }}>
                    <span className="badge bg-danger" style={{ fontSize: '0.7rem' }}>OFF</span>
                    <span className="fw-bold text-danger fs-6">{liveStatus.off}</span>
                </div>
                <div className="vr mx-1" style={{ height: 40 }} />
                <span className="text-muted small fw-semibold">CONNECTIVITY:</span>
                <div className="d-flex align-items-center gap-1 px-3 py-1 rounded" style={{ background: '#d1e7dd', border: '1px solid #a3cfbb' }}>
                    <i className="ti ti-wifi text-success" />
                    <span className="fw-bold text-success fs-6">{meterLiveStatus.online}</span>
                    <span className="text-success small">Online</span>
                </div>
                <div className="d-flex align-items-center gap-1 px-3 py-1 rounded" style={{ background: '#f8d7da', border: '1px solid #f1aeb5' }}>
                    <i className="ti ti-wifi-off text-danger" />
                    <span className="fw-bold text-danger fs-6">{meterLiveStatus.offline}</span>
                    <span className="text-danger small">Offline</span>
                </div>
                <button className="btn btn-sm btn-outline-secondary ms-auto" onClick={fetchFacilityCounts} title="Refresh counts">
                    <i className="ti ti-refresh" />
                </button>
            </div>
        );
    };

    return (
        <div className="card">
            <div className="card-body">
                <SummaryBar />
                <div className="row mb-3 align-items-center">
                    <div className="col-md-4 col-xs-12">
                        <input
                            className="form-control"
                            placeholder="Search by Device ID, Description, Serial No, Account No..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="col-md-8 col-xs-12 d-flex justify-content-end gap-2">
                        <button className="btn btn-outline-danger" onClick={handleExportPDF} disabled={exporting || !filteredMeters.length}>
                            <i className="ti ti-file-type-pdf me-1" /> Export PDF
                        </button>
                        <button className="btn btn-outline-success" onClick={handleExportExcel} disabled={exporting || !filteredMeters.length}>
                            <i className="ti ti-file-type-xls me-1" /> Export Excel
                        </button>
                    </div>
                </div>
                <div className="dt-responsive table-responsive">
                    <DataTable
                        value={filteredMeters}
                        loading={loading}
                        emptyMessage="No commercial meters found."
                        sortMode="multiple"
                        paginator
                        rows={100}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        stripedRows
                        tableStyle={{ minWidth: '50rem' }}
                        dataKey="_id"
                    >
                        <Column field="deviceId" header="Device ID" sortable />
                        <Column header="Description" body={descriptionTemplate} sortable sortField="unitName" />
                        <Column field="meterCategory" header="Category" sortable />
                        <Column field="accountNumber" header="Account Number" sortable />
                        <Column
                            field="previousMeterReading" header="Initial V (kWh)"
                            body={(r) => r.previousMeterReading != null ? `${r.previousMeterReading} kWh` : '-'}
                            sortable
                        />
                        <Column
                            field="currentMeterReading" header="Current V (kWh)"
                            body={(r) => r.currentMeterReading != null ? `${r.currentMeterReading} kWh` : '-'}
                            sortable
                        />
                        <Column field="liveStatus" header="Status" body={meterStatusTemplate} sortable />
                        <Column field="meterLiveStatus" header="Live Status" body={meterLiveStatusTemplate} sortable />
                        <Column
                            field="lastUpdated" header="Last Updated"
                            body={(r) => r.lastUpdated ? new Date(r.lastUpdated).toLocaleDateString() : '-'}
                            sortable
                        />
                        <Column header="Actions" body={actionsTemplate} />
                    </DataTable>
                </div>
            </div>
        </div>
    );
};

export default CommercialMetersTab;